import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	FormGroup,
	Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChartWhereFilterBuilder, { ChartWhereFilterPropertyOption } from './ChartWhereFilterBuilder';

export interface EntriesFilterFieldOption {
	id: string;
	label: string;
}

export interface EntriesFilterApplyPayload {
	where: string;
	includeArchived: boolean;
	/** Present only when `fieldOptions` was passed — `null` = all table columns. */
	visiblePropertyIds?: string[] | null;
}

interface EntriesFilterDialogProps {
	open: boolean;
	initialWhere: string;
	initialIncludeArchived: boolean;
	properties: ChartWhereFilterPropertyOption[];
	/** When set (non-empty), table column selection is shown. Omit on layouts without `EntriesTable` (e.g. mobile). */
	fieldOptions?: EntriesFilterFieldOption[];
	initialVisiblePropertyIds?: string[] | null;
	onClose: () => void;
	onApply: (payload: EntriesFilterApplyPayload) => void;
}

const EntriesFilterDialog = ({
	open,
	initialWhere,
	initialIncludeArchived,
	properties,
	fieldOptions,
	initialVisiblePropertyIds,
	onClose,
	onApply,
}: EntriesFilterDialogProps) => {
	const [where, setWhere] = useState('');
	const [includeArchived, setIncludeArchived] = useState(true);
	const [fieldsSectionExpanded, setFieldsSectionExpanded] = useState(false);
	const [selectedFieldIds, setSelectedFieldIds] = useState<Set<string>>(() => new Set());

	const fieldOptionsResolved = useMemo(
		() => (fieldOptions?.length ? fieldOptions : null),
		[fieldOptions],
	);
	const showFieldPicker = Boolean(fieldOptionsResolved?.length);

	const fieldSelectionButtonLabel = useMemo(() => {
		if (!fieldOptionsResolved?.length) {
			return '';
		}
		const total = fieldOptionsResolved.length;
		const selectedCount = fieldOptionsResolved.filter((o) => selectedFieldIds.has(o.id)).length;
		if (selectedCount === 0 || selectedCount === total) {
			return 'All';
		}
		return `${selectedCount} Field${selectedCount === 1 ? '' : 's'} selected`;
	}, [fieldOptionsResolved, selectedFieldIds]);

	useEffect(() => {
		if (!open) {
			return;
		}
		setWhere(initialWhere);
		setIncludeArchived(initialIncludeArchived);
		if (!fieldOptionsResolved) {
			return;
		}
		const validIds = new Set(fieldOptionsResolved.map((o) => o.id));
		const showAll = initialVisiblePropertyIds === null;
		if (showAll) {
			setSelectedFieldIds(new Set(fieldOptionsResolved.map((o) => o.id)));
			setFieldsSectionExpanded(false);
			return;
		}
		const fromStored = (initialVisiblePropertyIds ?? []).filter((id) => validIds.has(id));
		if (fromStored.length === 0 || fromStored.length === fieldOptionsResolved.length) {
			setSelectedFieldIds(new Set(fieldOptionsResolved.map((o) => o.id)));
			setFieldsSectionExpanded(false);
			return;
		}
		setSelectedFieldIds(new Set(fromStored));
		setFieldsSectionExpanded(true);
	}, [open, initialWhere, initialIncludeArchived, initialVisiblePropertyIds, fieldOptionsResolved]);

	const toggleField = useCallback((id: string, checked: boolean) => {
		setSelectedFieldIds((prev) => {
			const next = new Set(prev);
			if (checked) {
				next.add(id);
			} else {
				next.delete(id);
			}
			return next;
		});
	}, []);

	const handleApply = () => {
		const payload: EntriesFilterApplyPayload = { where, includeArchived };
		if (fieldOptionsResolved) {
			const ordered = fieldOptionsResolved.map((o) => o.id).filter((id) => selectedFieldIds.has(id));
			let visiblePropertyIds: string[] | null = null;
			if (ordered.length > 0 && ordered.length < fieldOptionsResolved.length) {
				visiblePropertyIds = ordered;
			}
			payload.visiblePropertyIds = visiblePropertyIds;
		}
		onApply(payload);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="body">
			<DialogTitle>Customize view</DialogTitle>
			<DialogContent>
				<Box mt={1}>
					<ChartWhereFilterBuilder
						chartId="repository-entries-filter"
						initialWhere={where}
						onChange={setWhere}
						properties={properties}
						includeArchived={includeArchived}
						onIncludeArchivedChange={setIncludeArchived}
						shouldAskAboutRelativeTime={false}
					/>
				</Box>
				{showFieldPicker && fieldOptionsResolved && (
					<>
						<Divider sx={{ my: 2 }} />
						<Box>
							<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
								Display fields
							</Typography>
							<Button
								type="button"
								variant="outlined"
								size="small"
								startIcon={fieldsSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
								onClick={() => setFieldsSectionExpanded((v) => !v)}
							>
								{fieldSelectionButtonLabel}
							</Button>
							<Collapse in={fieldsSectionExpanded}>
								<FormGroup sx={{ mt: 1.5, pl: 0.5 }}>
									{fieldOptionsResolved.map((opt) => (
										<FormControlLabel
											key={opt.id}
											control={
												<Checkbox
													size="small"
													checked={selectedFieldIds.has(opt.id)}
													onChange={(e) => toggleField(opt.id, e.target.checked)}
												/>
											}
											label={opt.label}
											sx={{
												m: 0,
												alignItems: 'center',
												'& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
											}}
										/>
									))}
								</FormGroup>
							</Collapse>
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button type="button" onClick={onClose}>
					Cancel
				</Button>
				<Button type="button" variant="contained" onClick={handleApply}>
					Apply
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default EntriesFilterDialog;
